
import {defineComponent} from 'vue'
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import BaseContainer from "@/components/UI/Bootstrap/BaseContainer.vue";
import InputFile from "@/components/UI/InputFile.vue";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import {
  PrepareCsvHandlingRequestRestDtoModel
} from "@/models/api/pharma-cpc-import/PrepareCsvHandlingRequestRestDtoModel";
import {ImportRestService} from "@/services/rest/cpc-import/ImportRestService";
import {
  PrepareXlsxHandlingRequestRestDtoModel
} from "@/models/api/pharma-cpc-import/PrepareXlsxHandlingRequestRestDtoModel";
import {
  AsyncHandlingResponseRestDto,
  HandleCsvRequestRestDto,
  HandleXlsxRequestRestDto,
  MappedColumnRestDto,
  PrepareImportHandlingResponseRestDto
} from "@/api/pharma-cpc-import/models";
import InputSelect from "@/components/UI/InputSelect.vue";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";

export default defineComponent({
  name: "ImportProducts",
  components: {
    InputSelect,
    InputCheckboxBoolean,
    InputFile,
    BaseContainer,
    AlertError2,
    BaseTitle,
  },
  props: {
    showBackRoute: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  data() {
    return {
      step: 'selection' as 'selection' | 'mapping',
      selectedFileType: '' as '' | 'csv' | 'xlsx',
      prepareCsvHandlingRequest: PrepareCsvHandlingRequestRestDtoModel.createWithDefaults(),
      prepareXlsxHandlingRequest: PrepareXlsxHandlingRequestRestDtoModel.createWithDefaults(),
      prepareImportHandlingResponse: null as PrepareImportHandlingResponseRestDto | null,
      handlingResponse: null as AsyncHandlingResponseRestDto | null,
      mappedColumns: [] as MappedColumnRestDto[],
      importCsvUI: UIStateDto.createWithDefaults(),
    }
  },
  mounted() {
    this.importCsvUI.setReady();
  },
  computed: {
    DpModuleEnum() {
      return DpModuleEnum;
    },
  },
  methods: {
    async submitSelection(): Promise<void> {
      this.importCsvUI
          .clearError()
          .setNotReady();

      try {
        if (this.selectedFileType === 'csv') {
          this.prepareImportHandlingResponse =
              await ImportRestService.getInstance().prepareCsvHandling(
                  this.prepareCsvHandlingRequest,
                  'IMPORT_PRODUCT_CUSTOMER_OVERRIDES'
              );
        } else {
          this.prepareImportHandlingResponse =
              await ImportRestService.getInstance().prepareXlsxHandling(
                  this.prepareXlsxHandlingRequest,
                  'IMPORT_PRODUCT_CUSTOMER_OVERRIDES'
              );
        }

        this.step = 'mapping';
        this.mappedColumns = [];
        for (let index: number = 0; index < this.prepareImportHandlingResponse!.headers.length; index++) {
          this.mappedColumns.push({
            column_index: index,
            column_type_code: 'UNDEFINED'
          });
        }
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.importCsvUI.setReady();
      }
    },
    goBack(): void {
      this.step = 'selection';
    },
    async submitMapping(): Promise<void> {
      this.importCsvUI
          .clearError()
          .setNotReady();

      try {
        const columns: MappedColumnRestDto[] = this.mappedColumns
            .filter((column: MappedColumnRestDto) => column.column_type_code !== 'UNDEFINED')
            .map((column: MappedColumnRestDto) => {
              return {
                column_index: column.column_index,
                column_type_code: column.column_type_code
              }
            }
            );

        if (this.selectedFileType === 'csv') {
          const request: HandleCsvRequestRestDto = {
            document_key: this.prepareCsvHandlingRequest.document_key,
            csv_metadata: this.prepareCsvHandlingRequest.csv_metadata,
            mapped_columns: columns
          };
          this.handlingResponse = await ImportRestService.getInstance().handleCsv(
              request,
              'IMPORT_PRODUCT_CUSTOMER_OVERRIDES'
          );
        } else {
          const request: HandleXlsxRequestRestDto = {
            document_key: this.prepareXlsxHandlingRequest.document_key,
            xlsx_metadata: this.prepareXlsxHandlingRequest.xlsx_metadata,
            mapped_columns: columns
          };
          this.handlingResponse = await ImportRestService.getInstance().handleXlsx(
              request,
              'IMPORT_PRODUCT_CUSTOMER_OVERRIDES'
          );
        }

        this.$router.push({
          name: 'async-handlings',
          params: {
            moduleCode: DpModuleEnum.CPC,
            pollingId: this.handlingResponse?.polling_id,
            backRoute: 'catalog-products-overview'
          }
        });
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.importCsvUI.setReady();
      }
    },
    mappedColumnUpdated(index: number): void {
      if (this.mappedColumns[index].column_type_code === null) {
        this.mappedColumns[index].column_type_code = 'UNDEFINED';
      }
    },
    setErrorMessage(error: any): void {
      if (error instanceof String) {
        this.importCsvUI.setError(error as string);
        return;
      }

      this.setExceptions(error);
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
          .create()
          .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.importCsvUI as UIStateDto))
          .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.importCsvUI as UIStateDto))
          .catch(exceptions, true);
    },
  }
})
